import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  addInvestorNote,
  deleteInvestor,
  deleteInvestorLogo,
  deleteInvestorNote,
  getInvestor,
  listUsers,
  updateInvestor,
  uploadInvestorLogo,
  type Investor,
  type InvestorFields,
  type InvestorInstrument,
  type InvestorStage,
  type InvestorType,
} from "@/lib/api";
import {
  CONFIDENCE_LABEL,
  INSTRUMENT_LABEL,
  INVESTOR_STAGE_COLOR,
  INVESTOR_STAGE_COLUMNS,
  INVESTOR_TYPE_LABEL,
  fmtDate,
  fmtMoney,
  impliedValuation,
  isDilutive,
  isoToday,
} from "@/lib/investors";
import { EditableLogo } from "@/components/EntityLogo";

const inputCls =
  "w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";
const labelCls = "mb-1 block text-xs font-medium text-text-muted";

/**
 * One investor, end to end: who they are, what they're offering, where the
 * conversation stands, and every touchpoint so far.
 *
 * Terms are stored raw and the valuation is derived — a number typed by hand
 * is one nobody checks.
 */
export function InvestorDrawer({
  investor,
  onClose,
}: {
  investor: Investor;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  // The list row already carries every editable field, so the form seeds from
  // it and this fetch is only for the activity log — nothing to reconcile.
  const { data: detail } = useQuery({
    queryKey: ["investor", investor.id],
    queryFn: () => getInvestor(investor.id),
  });
  const { data: users } = useQuery({ queryKey: ["users"], queryFn: listUsers });

  const [form, setForm] = useState<Investor>(investor);
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);

  const set = <K extends keyof Investor>(key: K, value: Investor[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setDirty(true);
  };

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["investors"] });
    qc.invalidateQueries({ queryKey: ["investor", investor.id] });
  };

  const save = useMutation({
    mutationFn: () => updateInvestor(investor.id, toFields(form)),
    onSuccess: () => {
      invalidate();
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    },
    // Without this a failed save looked identical to a no-op: the button
    // stayed live, nothing appeared, and the edit was quietly lost.
    onError: () => setSaved(false),
  });

  const remove = useMutation({
    mutationFn: () => deleteInvestor(investor.id),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const logoUpload = useMutation({
    mutationFn: (file: File) => uploadInvestorLogo(investor.id, file),
    onSuccess: invalidate,
  });
  const logoRemove = useMutation({
    mutationFn: () => deleteInvestorLogo(investor.id),
    onSuccess: invalidate,
  });

  const requestClose = useCallback(() => {
    if (!dirty || confirm("Discard unsaved changes?")) onClose();
  }, [dirty, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && requestClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [requestClose]);

  const dilutive = isDilutive(form.instrument);
  const valuation = impliedValuation(
    form.amount,
    form.equityPct,
    form.valuationBasis,
    form.instrument,
  );

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4 sm:p-8"
      onClick={requestClose}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-token-lg border border-border bg-bg-elevated shadow-token-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center gap-3 border-b border-border px-5 py-3">
          <EditableLogo
            name={form.name}
            url={investor.logoUrl}
            size={38}
            fallbackIcon="fa-sack-dollar"
            uploading={logoUpload.isPending}
            onUpload={(f) => logoUpload.mutate(f)}
            onRemove={() => logoRemove.mutate()}
          />
          <div className="min-w-0 flex-1">
            <input
              className="w-full bg-transparent font-display text-lg font-semibold text-text-heading outline-none"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
            />
            <span className="flex items-center gap-2 text-xs text-text-muted">
              <span
                className="h-2 w-2 rounded-full"
                style={{ background: INVESTOR_STAGE_COLOR[form.stage] }}
              />
              {INVESTOR_TYPE_LABEL[form.type]}
              {form.round && ` · ${form.round}`}
            </span>
          </div>
          <button
            onClick={() => {
              if (confirm(`Delete ${form.name}? This cannot be undone.`)) remove.mutate();
            }}
            className="rounded-md px-2 py-1 text-xs text-danger hover:bg-danger/10"
          >
            <i className="fa-solid fa-trash mr-1" />
            Delete
          </button>
          <button
            onClick={requestClose}
            className="grid h-7 w-7 place-items-center rounded-md text-text-muted hover:bg-bg-card-hover hover:text-text-heading"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {/* ── Terms ── */}
          <Section title="Terms" icon="fa-handshake">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Field label="Amount (£)">
                <input
                  className={inputCls}
                  type="number"
                  min="0"
                  value={form.amount ?? ""}
                  onChange={(e) => set("amount", numOrNull(e.target.value))}
                />
              </Field>
              <Field label="Equity wanted (%)">
                <input
                  className={inputCls}
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  disabled={!dilutive}
                  title={dilutive ? undefined : "This instrument costs no equity"}
                  value={dilutive ? form.equityPct ?? "" : ""}
                  onChange={(e) => set("equityPct", numOrNull(e.target.value))}
                />
              </Field>
              <Field label="Instrument">
                <select
                  className={inputCls}
                  value={form.instrument}
                  onChange={(e) => set("instrument", e.target.value as InvestorInstrument)}
                >
                  {Object.entries(INSTRUMENT_LABEL).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Valuation basis">
                <select
                  className={inputCls}
                  disabled={!dilutive}
                  value={form.valuationBasis}
                  onChange={(e) =>
                    set("valuationBasis", e.target.value as Investor["valuationBasis"])
                  }
                >
                  <option value="post">% of post-money</option>
                  <option value="pre">% of pre-money</option>
                </select>
              </Field>
              <Field label="Length of support (months)">
                <input
                  className={inputCls}
                  type="number"
                  min="0"
                  value={form.supportMonths ?? ""}
                  onChange={(e) => set("supportMonths", numOrNull(e.target.value))}
                />
              </Field>
              <Field label="Cost to us (£)">
                <input
                  className={inputCls}
                  type="number"
                  min="0"
                  placeholder="fees, match funding, legals"
                  value={form.costs ?? ""}
                  onChange={(e) => set("costs", numOrNull(e.target.value))}
                />
              </Field>
            </div>

            <div className="mt-3 rounded-token-md border border-border-light bg-bg-secondary/60 px-3 py-2 text-xs">
              {valuation ? (
                <span className="text-text-secondary">
                  Implies a{" "}
                  <strong className="text-text-heading">{fmtMoney(valuation.pre)}</strong>{" "}
                  pre-money valuation ·{" "}
                  <strong className="text-text-heading">{fmtMoney(valuation.post)}</strong>{" "}
                  post-money
                </span>
              ) : (
                <span className="text-text-muted">
                  {dilutive
                    ? "Set an amount and an equity % to see the implied valuation."
                    : `${INSTRUMENT_LABEL[form.instrument]} costs no equity — no valuation implied.`}
                </span>
              )}
            </div>

            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="What else they bring">
                <textarea
                  className={inputCls}
                  rows={3}
                  placeholder="Board seat, advisory hours, intros, office space…"
                  value={form.nonFinancialTerms ?? ""}
                  onChange={(e) => set("nonFinancialTerms", e.target.value || null)}
                />
              </Field>
              <Field label="Conditions & tranches">
                <textarea
                  className={inputCls}
                  rows={3}
                  placeholder="Half now, half at £X ARR…"
                  value={form.conditions ?? ""}
                  onChange={(e) => set("conditions", e.target.value || null)}
                />
              </Field>
            </div>
          </Section>

          {/* ── Status ── */}
          <Section title="Where it stands" icon="fa-signs-post">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Field label="Stage">
                <select
                  className={inputCls}
                  value={form.stage}
                  onChange={(e) => set("stage", e.target.value as InvestorStage)}
                >
                  {INVESTOR_STAGE_COLUMNS.map((s) => (
                    <option key={s.stage} value={s.stage}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Confidence">
                <select
                  className={inputCls}
                  value={form.confidence}
                  onChange={(e) => set("confidence", e.target.value as Investor["confidence"])}
                >
                  {Object.entries(CONFIDENCE_LABEL).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Round">
                <input
                  className={inputCls}
                  placeholder="Pre-seed"
                  value={form.round ?? ""}
                  onChange={(e) => set("round", e.target.value || null)}
                />
              </Field>
              <Field label="Next step">
                <input
                  className={inputCls}
                  placeholder="Send the deck"
                  value={form.nextStep ?? ""}
                  onChange={(e) => set("nextStep", e.target.value || null)}
                />
              </Field>
              <Field label="Next step by">
                <input
                  className={inputCls}
                  type="date"
                  value={form.nextStepOn ?? ""}
                  onChange={(e) => set("nextStepOn", e.target.value || null)}
                />
              </Field>
              <Field label="Their deadline">
                <input
                  className={inputCls}
                  type="date"
                  value={form.decisionBy ?? ""}
                  onChange={(e) => set("decisionBy", e.target.value || null)}
                />
              </Field>
              <Field label="Last contacted">
                <input
                  className={inputCls}
                  type="date"
                  value={form.lastContactedOn ?? ""}
                  onChange={(e) => set("lastContactedOn", e.target.value || null)}
                />
              </Field>
              <Field label="Revisit on">
                <input
                  className={inputCls}
                  type="date"
                  title="&quot;Come back at £30k MRR&quot; — parks the card without passing"
                  value={form.revisitOn ?? ""}
                  onChange={(e) => set("revisitOn", e.target.value || null)}
                />
              </Field>
              <Field label="Owner">
                <select
                  className={inputCls}
                  value={form.ownerId ?? ""}
                  onChange={(e) => set("ownerId", e.target.value || null)}
                >
                  <option value="">Unassigned</option>
                  {users?.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name || u.email}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            {form.stage === "passed" && (
              <Field label="Why they passed" className="mt-3">
                <textarea
                  className={inputCls}
                  rows={2}
                  placeholder="Too early, wrong sector, no lead investor…"
                  value={form.passReason ?? ""}
                  onChange={(e) => set("passReason", e.target.value || null)}
                />
              </Field>
            )}
          </Section>

          {/* ── Contact ── */}
          <Section title="Who they are" icon="fa-address-card">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Field label="Type">
                <select
                  className={inputCls}
                  value={form.type}
                  onChange={(e) => set("type", e.target.value as InvestorType)}
                >
                  {Object.entries(INVESTOR_TYPE_LABEL).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Contact name">
                <input
                  className={inputCls}
                  value={form.contactName ?? ""}
                  onChange={(e) => set("contactName", e.target.value || null)}
                />
              </Field>
              <Field label="Their role">
                <input
                  className={inputCls}
                  value={form.contactRole ?? ""}
                  onChange={(e) => set("contactRole", e.target.value || null)}
                />
              </Field>
              <Field label="Email">
                <input
                  className={inputCls}
                  type="email"
                  value={form.contactEmail ?? ""}
                  onChange={(e) => set("contactEmail", e.target.value || null)}
                />
              </Field>
              <Field label="Phone">
                <input
                  className={inputCls}
                  value={form.contactPhone ?? ""}
                  onChange={(e) => set("contactPhone", e.target.value || null)}
                />
              </Field>
              <Field label="Website">
                <input
                  className={inputCls}
                  placeholder="https://"
                  value={form.website ?? ""}
                  onChange={(e) => set("website", e.target.value || null)}
                />
              </Field>
            </div>
            <Field label="How we know them" className="mt-3">
              <input
                className={inputCls}
                placeholder="Warm intro from…, met at…, cold outreach"
                value={form.source ?? ""}
                onChange={(e) => set("source", e.target.value || null)}
              />
            </Field>
            <Field label="Notes" className="mt-3">
              <textarea
                className={inputCls}
                rows={3}
                value={form.notes ?? ""}
                onChange={(e) => set("notes", e.target.value || null)}
              />
            </Field>
          </Section>

          <ActivitySection
            investorId={investor.id}
            notes={detail?.activity}
            // Logging a note stamps lastContactedOn server-side. Without
            // mirroring it here the form still holds the old date and the
            // next save silently rolls it back.
            onLogged={(date) => setForm((f) => ({ ...f, lastContactedOn: date }))}
          />
        </div>

        <footer className="flex items-center justify-end gap-3 border-t border-border px-5 py-3">
          <span className="text-xs">
            {save.isError ? (
              <span className="text-danger">
                {save.error instanceof ApiError
                  ? save.error.message
                  : "Save failed — your changes are still here, try again."}
              </span>
            ) : saved ? (
              <span className="text-accent">Saved</span>
            ) : dirty ? (
              <span className="text-text-muted">Unsaved changes</span>
            ) : null}
          </span>
          {/*
            Only disabled while a save is actually in flight. Gating it on
            unsaved changes left the button dead after every save, which reads
            as broken — and re-saving an unchanged record costs nothing.
          */}
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ color: "var(--color-on-accent)" }}
          >
            {save.isPending ? "Saving…" : "Save changes"}
          </button>
        </footer>
      </div>
    </div>
  );
}

/* ── Activity log ───────────────────────────────────────────────────────── */

function ActivitySection({
  investorId,
  notes,
  onLogged,
}: {
  investorId: string;
  notes: { id: string; date: string; description: string }[] | undefined;
  onLogged: (date: string) => void;
}) {
  const qc = useQueryClient();
  const [date, setDate] = useState(isoToday());
  const [description, setDescription] = useState("");

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["investor", investorId] });
    qc.invalidateQueries({ queryKey: ["investors"] });
  };

  const add = useMutation({
    mutationFn: () => addInvestorNote(investorId, { date, description }),
    onSuccess: (note) => {
      setDescription("");
      onLogged(note.date);
      invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteInvestorNote(id),
    onSuccess: invalidate,
  });

  return (
    <Section title="Activity" icon="fa-clock-rotate-left">
      <form
        className="mb-3 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (description.trim()) add.mutate();
        }}
      >
        <input
          className={`${inputCls} w-36 shrink-0`}
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        <input
          className={inputCls}
          placeholder="Called about the deck — asked for our runway numbers"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <button
          type="submit"
          disabled={!description.trim() || add.isPending}
          className="shrink-0 rounded-token-md border border-border px-3 py-2 text-sm text-text-secondary hover:text-text-heading disabled:opacity-50"
        >
          Log
        </button>
      </form>

      {notes?.length ? (
        <ul className="space-y-1.5">
          {notes.map((n) => (
            <li
              key={n.id}
              className="group flex items-start gap-3 rounded-token-md border border-border-light px-3 py-2 text-sm"
            >
              <span className="shrink-0 text-xs text-text-muted">{fmtDate(n.date)}</span>
              <span className="min-w-0 flex-1 text-text-secondary">{n.description}</span>
              <button
                onClick={() => remove.mutate(n.id)}
                className="shrink-0 text-xs text-text-muted opacity-0 transition-opacity hover:text-danger group-hover:opacity-100"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-text-muted">
          Nothing logged yet. Every call and email here is what stops this going quiet.
        </p>
      )}
    </Section>
  );
}

/* ── Layout helpers ─────────────────────────────────────────────────────── */

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-6">
      <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
        <i className={`fa-solid ${icon} text-[11px]`} />
        {title}
      </h3>
      {children}
    </section>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );
}

/** Empty numeric inputs clear the field rather than saving 0. */
const numOrNull = (raw: string): number | null => (raw === "" ? null : Number(raw));

/** Only the editable columns — never the derived or server-owned ones. */
function toFields(f: Investor): InvestorFields {
  return {
    name: f.name,
    type: f.type,
    website: f.website,
    contactName: f.contactName,
    contactRole: f.contactRole,
    contactEmail: f.contactEmail,
    contactPhone: f.contactPhone,
    source: f.source,
    ownerId: f.ownerId,
    amount: f.amount,
    equityPct: isDilutive(f.instrument) ? f.equityPct : null,
    instrument: f.instrument,
    valuationBasis: f.valuationBasis,
    supportMonths: f.supportMonths,
    nonFinancialTerms: f.nonFinancialTerms,
    conditions: f.conditions,
    costs: f.costs,
    round: f.round,
    stage: f.stage,
    confidence: f.confidence,
    nextStep: f.nextStep,
    nextStepOn: f.nextStepOn,
    lastContactedOn: f.lastContactedOn,
    decisionBy: f.decisionBy,
    revisitOn: f.revisitOn,
    passReason: f.passReason,
    notes: f.notes,
  };
}
